import { logger } from '@/services/Logger'
import ChatTextInputSection from './internal/ChatTextInputSection'
import ChatMessagesSection from './internal/ChatMessagesSection'
import { formatBytes } from '@/utils'
import ChatRoomSnackbar from './internal/ChatRoomSnackbar'
import MessageBoxFileUsageProgressbar from './internal/FileUsageProgressBar'
import { cn } from '@/utlis/cn'
import useChatRoomController from './useChatRoomController'
import { useDropzone } from 'react-dropzone'
import * as Constants from '@/services/Constants'
import { useTranslation } from 'react-i18next'

const log = logger.createChildLogger({ name: 'ChatRoom' })

export type ChatboxTabTypes = 'messages' | 'files'

export type ChatboxSnackbarInfo = {
    message: string
    severity: 'success' | 'info' | 'warning' | 'error'
}

export default function ChatRoom({
    chatController,
    currentTab,
}: {
    chatController: ReturnType<typeof useChatRoomController>
    currentTab: ChatboxTabTypes
}) {
    const { t } = useTranslation()

    const {
        chatManager,
        messageHistory,
        msgLoadedBatchState,
        chatboxSnackbar,
        currentSelectedFiles,
        scrollContainerRef,
        downloadLockedFilesRef,
        doBlockDragModeRef,
        setChatboxSnackbar,
        onCellSelectedHandler,
        onDeleteSelectedCellsHandler,
    } = chatController

    const onDrop = (file: any) => {
        // where type is the mime type, e.g. 'application/json'.
        if (Array.isArray(file) && file.length > Constants.max_simultaneous_chatbox_files_ct) {
            setChatboxSnackbar({
                severity: 'error',
                message:
                    `You can only drag and drop ` +
                    `up to ${Constants.max_simultaneous_chatbox_files_ct} files at once`,
            })
        } else {
            chatManager.sendFile(file)
        }
    }
    const { getRootProps, isDragActive } = useDropzone({ onDrop })

    if (chatController.chatManager == null || chatController.chatManager.isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div className="text-white">Loading...</div>
            </div>
        )
    }

    const stats = chatController.chatManager.stats

    log.verbose('ChatRoom', 'rendering')

    return (
        <div id="dropzone-container" className="grid grid-rows-[1fr_min-content] w-full h-[500px]" {...getRootProps()}>
            <div id="chatbox-container" className={cn('relative h-full w-full flex flex-col overflow-hidden')}>
                <ChatRoomSnackbar data={chatboxSnackbar} />
                <ChatMessagesSection
                    chatManager={chatManager}
                    currentTab={currentTab}
                    messageHistory={messageHistory}
                    msgLoadedBatchState={msgLoadedBatchState}
                    currentSelectedFiles={currentSelectedFiles}
                    scrollContainerRef={scrollContainerRef}
                    downloadLockedFilesRef={downloadLockedFilesRef}
                    doBlockDragModeRef={doBlockDragModeRef}
                    onCellSelectedHandler={onCellSelectedHandler}
                    onDeleteSelectedCellsHandler={onDeleteSelectedCellsHandler}
                />

                <div>
                    {currentTab === 'messages' ? (
                        <ChatTextInputSection chatManager={chatManager} />
                    ) : (
                        <MessageBoxFileUsageProgressbar
                            value={((stats.fileSpaceUsage || 0) / (stats.fileStorageSize || 1)) * 100}
                            spaceUsed={formatBytes(stats.fileSpaceUsage)}
                            spaceTotal={formatBytes(stats.fileStorageSize)}
                        />
                    )}
                </div>
                {isDragActive && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-neutral-400/50 rounded-[20px]">
                        <p className="text-[16px] text-shadow-lg text-shadow-black/30">{t('Drop file here')}</p>
                    </div>
                )}
            </div>
        </div>
    )
}
